import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api } from "../api";

const SECTION_KEYS = [
  ["challenge", "The Challenge"],
  ["objective", "Objective"],
  ["strategy", "Strategy"],
  ["execution", "Execution"],
  ["conclusion", "Conclusion"],
];

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/case-studies/${slug}`)
      .then((r) => setCs(r.data.case_study))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="case-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-100">Case study not found</h1>
        <Link to="/work" className="mt-6 inline-flex items-center gap-2 text-blue-400 hover:text-blue-300">
          <ArrowLeft size={15} /> Back to Our Work
        </Link>
      </main>
    );
  }

  if (!cs) {
    return (
      <main className="pt-40 pb-32 flex justify-center" data-testid="case-loading">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24" data-testid="case-detail-page">
      <Seo title={cs.title} description={(cs.challenge || "").slice(0, 155)} />
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Link to="/work" data-testid="case-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-blue-300 transition-colors">
          <ArrowLeft size={15} /> All case studies
        </Link>
        <div className="mt-8 flex flex-wrap items-center gap-2.5">
          <span className="font-mono-accent text-[10px] uppercase tracking-widest text-blue-300 border border-blue-500/30 rounded-full px-3 py-1">{cs.industry}</span>
          {(cs.categories || []).map((c) => (
            <span key={c} className="font-mono-accent text-[10px] uppercase tracking-widest text-slate-400 border border-slate-700 rounded-full px-3 py-1">{c}</span>
          ))}
          {cs.is_demo && (
            <span className="font-mono-accent text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/30 rounded-full px-3 py-1">Illustrative demo</span>
          )}
        </div>
        <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-50 leading-[1.05]" data-testid="case-title">
          {cs.title}
        </h1>
        {cs.client && <p className="mt-4 text-sm text-slate-500">Client: {cs.client}</p>}

        {(cs.results || []).length > 0 && (
          <Reveal className="mt-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="case-results">
              {cs.results.map((r) => (
                <div key={r.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 text-center">
                  <p className="font-display text-2xl sm:text-3xl font-extrabold text-gradient">{r.value}</p>
                  <p className="mt-1.5 font-mono-accent text-[10px] uppercase tracking-widest text-slate-500">{r.label}</p>
                </div>
              ))}
            </div>
            {cs.is_demo && <p className="mt-3 text-[11px] text-slate-600">Results shown are illustrative demo figures, not verified client data.</p>}
          </Reveal>
        )}

        <div className="mt-14 space-y-10">
          {SECTION_KEYS.map(([key, label]) =>
            cs[key] ? (
              <Reveal key={key}>
                <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-50">{label}</h2>
                <p className="mt-3 text-base text-slate-400 leading-relaxed">{cs[key]}</p>
              </Reveal>
            ) : null
          )}
          {(cs.highlights || []).length > 0 && (
            <Reveal>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-50">Campaign Highlights</h2>
              <ul className="mt-4 space-y-2.5">
                {cs.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-base text-slate-400">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" /> {h}
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
          {cs.testimonial_quote && (
            <Reveal>
              <blockquote className="rounded-2xl border border-blue-500/25 bg-blue-600/5 p-8" data-testid="case-testimonial">
                <Quote size={22} className="text-blue-400" />
                <p className="mt-4 text-lg text-slate-200 leading-relaxed">{cs.testimonial_quote}</p>
                {cs.testimonial_author && <cite className="mt-4 block text-sm text-slate-500 not-italic">— {cs.testimonial_author}</cite>}
              </blockquote>
            </Reveal>
          )}
        </div>

        <Reveal className="mt-16">
          <div className="glass-panel rounded-2xl p-8 sm:p-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-50">Want results like these?</h2>
            <p className="mt-3 text-slate-400">Tell us about your brand and goals. We will build the plan.</p>
            <Link
              to="/start"
              data-testid="case-cta-btn"
              className="mt-7 group inline-flex items-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-all duration-300 active:scale-95"
            >
              Start a Project <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
